import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../utils/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  Eye,
  EyeOff,
  Copy,
  Check,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  validatePassword,
  defaultRequirements,
  PasswordStrength,
} from "../../utils/password-validation";
import { PasswordRequirements } from "../../components/PasswordRequirements";
import { PasswordStrengthIndicator } from "../../components/PasswordStrengthIndicator";

// ===================================================================
// Account Setup Page — No sidebar, standalone page for new hires
// ===================================================================
const AccountSetup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const email = searchParams.get("email") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(
    validatePassword("")
  );

  // Page-load validation of the setup link:
  // 'checking' → validating, 'valid' → show the password form, 'invalid' →
  // bad/expired link, 'already_setup' → password already created, go log in.
  const [linkState, setLinkState] = useState<
    "checking" | "valid" | "invalid" | "already_setup"
  >("checking");

  useEffect(() => {
    if (!token) return; // handled by the missing-token render below
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{
          success: boolean;
          valid: boolean;
          reason?: string;
        }>(
          `/api/users/onboarding-setup/validate?email=${encodeURIComponent(
            email
          )}&token=${encodeURIComponent(token)}`,
          { skipErrorToast: true, skipAuthHandler: true }
        );
        if (cancelled) return;
        if (res.valid) setLinkState("valid");
        else setLinkState(res.reason === "already_setup" ? "already_setup" : "invalid");
      } catch {
        if (!cancelled) setLinkState("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, email]);

  // Accounts that already completed setup are taken straight to login.
  useEffect(() => {
    if (linkState !== "already_setup") return;
    const timer = setTimeout(() => navigate("/login"), 5000);
    return () => clearTimeout(timer);
  }, [linkState, navigate]);

  // Password requirements checklist
  const passwordRequirements = [
    `At least ${defaultRequirements.minLength} characters`,
    "At least one lowercase letter",
    "At least one uppercase letter",
    "At least one number",
    "At least one special character",
  ];

  const passwordMetRequirements = [
    password.length >= defaultRequirements.minLength,
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /\d/.test(password),
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  ];

  // Validate password on change
  useEffect(() => {
    setPasswordValidation(validatePassword(password));
  }, [password]);

  // -----------------------------------------------------------------
  // Copy email to clipboard
  // -----------------------------------------------------------------
  const handleCopyEmail = () => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // -----------------------------------------------------------------
  // Submit password setup
  // -----------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!passwordValidation.isValid) {
      toast.error("Password does not meet all requirements");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      const res = await api.post<{
        success: boolean;
        message: string;
        token?: string;
        user?: any;
        permissions?: string[];
      }>("/api/users/onboarding-setup", { token, email, password });

      if (res.success && res.token && res.user) {
        setShowSuccessModal(true);
        // Swap the session cleanly — any previous session in this browser
        // (e.g. an HR admin logged in on another tab) must not survive setup.
        localStorage.setItem("token", res.token);
        localStorage.setItem("user", JSON.stringify(res.user));
        localStorage.setItem("permissions", JSON.stringify(res.permissions || []));
        // Full page load so AuthContext boots fresh as the new hire.
        setTimeout(() => {
          window.location.assign("/my-onboarding");
        }, 2000);
      } else {
        toast.error(res.message || "Failed to set up account");
      }
    } catch (error: any) {
      if (error?.data?.code === "ACCOUNT_ALREADY_SETUP") {
        setLinkState("already_setup");
        return;
      }
      toast.error(error?.message || "Failed to set up account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // A valid setup link always carries the single-use token from the welcome
  // email — without it there is nothing to verify the request against.
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 p-8 shadow-sm text-center">
          <h2 className="text-xl font-semibold text-gray-900">Invalid setup link</h2>
          <p className="text-sm text-gray-500 mt-2">
            This account setup link is missing its security token. Please use the
            full link from your welcome email, or ask HR to send you a new one.
          </p>
        </div>
      </div>
    );
  }

  if (linkState === "checking") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 p-8 shadow-sm text-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto" />
          <p className="text-sm text-gray-500 mt-3">Checking your setup link...</p>
        </div>
      </div>
    );
  }

  if (linkState === "already_setup") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 p-8 shadow-sm text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-emerald-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Account already set up</h2>
          <p className="text-sm text-gray-500 mt-2">
            You&apos;ve already created a password for this account. Log in with
            your email and password to continue your onboarding.
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="w-full mt-6 bg-gray-900 hover:bg-gray-800 text-white h-11"
          >
            Go to login
          </Button>
          <p className="text-xs text-gray-400 mt-3">
            Redirecting you to the login page...
          </p>
        </div>
      </div>
    );
  }

  if (linkState === "invalid") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 p-8 shadow-sm text-center">
          <h2 className="text-xl font-semibold text-gray-900">Invalid or expired link</h2>
          <p className="text-sm text-gray-500 mt-2">
            This account setup link is invalid or has expired. Please use the
            full link from your welcome email, or ask HR to send you a new one.
          </p>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">idarah</h1>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 text-center">
            Set a password to finish onboarding
          </h2>
          <p className="text-sm text-gray-500 text-center mt-2">
            You&apos;re almost in. Create a password to secure your account and you&apos;ll be ready to go.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            {/* Username / Email */}
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Your username
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="email"
                  type="text"
                  value={email}
                  readOnly
                  className="pr-10 bg-gray-50 text-gray-600"
                />
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Copy email"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {password && (
                <PasswordStrengthIndicator
                  strength={passwordValidation.strength}
                  color={passwordValidation.color}
                />
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                Confirm password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pr-10"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Password requirements
              </p>
              <PasswordRequirements
                requirements={passwordRequirements}
                metRequirements={passwordMetRequirements}
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white h-11"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Complete setup
            </Button>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-xl animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">You&apos;re all set</h3>
            <p className="text-sm text-gray-500 mt-2">
              Your account is secured. Taking you to the next step...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSetup;
